"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@/utils/cn";
import { CalendarIcon } from "lucide-react";
import { DatePicker as AriaDatePicker, DateRangePicker as AriaDateRangePicker, DatePickerProps, DateRangePickerProps, DateValue, Dialog, Text, ValidationResult } from "react-aria-components";
import { Button } from "./button";
import { CalendarCell, CalendarGrid, CalendarGridBody, CalendarGridHeader, CalendarHeaderCell, CalendarHeading, JollyCalendar, RangeCalendar } from "./calendar";
import { DateInput, DateSegment } from "./datefield";
import { FieldGroup, Label } from "./field";
import { Popover } from "./popover";
interface JollyDatePickerProps<T extends DateValue> extends Omit<DatePickerProps<T>, "granularity"> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  className?: string;
  contentClassName?: string;
  granularity?: "year" | "month" | "day";
}
function JollyDatePicker(t0) {
  const $ = _c(37);
  let className;
  let contentClassName;
  let description;
  let errorMessage;
  let granularity;
  let label;
  let props;
  if ($[0] !== t0) {
    ({
      label,
      description,
      errorMessage,
      className,
      contentClassName,
      granularity,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = contentClassName;
    $[3] = description;
    $[4] = errorMessage;
    $[5] = granularity;
    $[6] = label;
    $[7] = props;
  } else {
    className = $[1];
    contentClassName = $[2];
    description = $[3];
    errorMessage = $[4];
    granularity = $[5];
    label = $[6];
    props = $[7];
  }
  const mode = granularity === "year" ? "year" : granularity === "month" ? "month" : "day";
  const t1 = granularity as any;
  let t2;
  if ($[8] !== className) {
    t2 = cn("group flex flex-col gap-1", className);
    $[8] = className;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== label) {
    t3 = label && <Label className="text-title-medium mb-2">{label}</Label>;
    $[10] = label;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== granularity) {
    t4 = <DateInput className="flex-1 px-2">{segment => {
        if (granularity === "month" && segment.type !== "month") {
          return <></>;
        }
        if (granularity === "year" && segment.type !== "year") {
          return <></>;
        }
        return <DateSegment segment={segment} className={cn("unfocusable", segment.isPlaceholder && "uppercase")} />;
      }}</DateInput>;
    $[12] = granularity;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Button variant="ghost" size="icon" className="mr-1 size-6 data-[focus-visible]:ring-0 data-[focus-visible]:ring-offset-0"><CalendarIcon aria-hidden={true} className="size-4" /></Button>;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4) {
    t6 = <FieldGroup>{t4}{t5}</FieldGroup>;
    $[15] = t4;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== description) {
    t7 = description && <Text slot="description" className="text-body-small">{description}</Text>;
    $[17] = description;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = typeof errorMessage === "string" ? errorMessage : "";
  let t9;
  if ($[19] !== t8) {
    t9 = <Text slot="errorMessage" className="text-body-small-medium text-destructive h-4">{t8}</Text>;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== contentClassName) {
    t10 = cn("p-4", contentClassName);
    $[21] = contentClassName;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== mode) {
    t11 = <JollyCalendar mode={mode} />;
    $[23] = mode;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <Popover placement="bottom start"><Dialog className={t10}>{t11}</Dialog></Popover>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== props || $[29] !== t1 || $[30] !== t12 || $[31] !== t2 || $[32] !== t3 || $[33] !== t6 || $[34] !== t7 || $[35] !== t9) {
    t13 = <AriaDatePicker {...props} granularity={t1} className={t2}>{t3}{t6}{t7}{t9}{t12}</AriaDatePicker>;
    $[28] = props;
    $[29] = t1;
    $[30] = t12;
    $[31] = t2;
    $[32] = t3;
    $[33] = t6;
    $[34] = t7;
    $[35] = t9;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}
interface JollyDateRangePickerProps<T extends DateValue> extends DateRangePickerProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  className?: string;
  contentClassName?: string;
}
function JollyDateRangePicker(t0) {
  const $ = _c(30);
  let className;
  let contentClassName;
  let description;
  let errorMessage;
  let label;
  let props;
  if ($[0] !== t0) {
    ({
      label,
      description,
      errorMessage,
      className,
      contentClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = contentClassName;
    $[3] = description;
    $[4] = errorMessage;
    $[5] = label;
    $[6] = props;
  } else {
    className = $[1];
    contentClassName = $[2];
    description = $[3];
    errorMessage = $[4];
    label = $[5];
    props = $[6];
  }
  let t1;
  if ($[7] !== className) {
    t1 = cn("group flex flex-col gap-1", className);
    $[7] = className;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== label) {
    t2 = label && <Label className="text-title-medium">{label}</Label>;
    $[9] = label;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex items-center gap-1"><DateInput slot="start" className="px-2">{_temp}</DateInput><span aria-hidden="true" className="text-muted-foreground">–</span><DateInput slot="end" className="flex-1 px-2">{_temp2}</DateInput></div>;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FieldGroup className="flex items-center justify-between">{t3}<Button variant="ghost" size="icon" className="mr-1 size-6 data-[focus-visible]:ring-0 data-[focus-visible]:ring-offset-0"><CalendarIcon aria-hidden={true} className="size-4" /></Button></FieldGroup>;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== description) {
    t5 = description && <Text slot="description" className="text-body-small">{description}</Text>;
    $[13] = description;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = typeof errorMessage === "string" ? errorMessage : "";
  let t7;
  if ($[15] !== t6) {
    t7 = <Text slot="errorMessage" className="text-body-small-medium text-destructive h-4">{t6}</Text>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== contentClassName) {
    t8 = cn("p-4 outline-none", contentClassName);
    $[17] = contentClassName;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <CalendarHeading mode="day" />;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <RangeCalendar>{t9}<CalendarGrid><CalendarGridHeader>{_temp3}</CalendarGridHeader><CalendarGridBody>{_temp4}</CalendarGridBody></CalendarGrid></RangeCalendar>;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t8) {
    t11 = <Popover placement="bottom start" className="w-auto"><Dialog className={t8}>{t10}</Dialog></Popover>;
    $[21] = t8;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== props || $[24] !== t1 || $[25] !== t11 || $[26] !== t2 || $[27] !== t5 || $[28] !== t7) {
    t12 = <AriaDateRangePicker {...props} className={t1}>{t2}{t4}{t5}{t7}{t11}</AriaDateRangePicker>;
    $[23] = props;
    $[24] = t1;
    $[25] = t11;
    $[26] = t2;
    $[27] = t5;
    $[28] = t7;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}
function _temp4(date) {
  return <CalendarCell date={date} />;
}
function _temp3(day) {
  return <CalendarHeaderCell>{day}</CalendarHeaderCell>;
}
function _temp2(segment_0) {
  return <DateSegment segment={segment_0} />;
}
function _temp(segment) {
  return <DateSegment segment={segment} />;
}
export { JollyDatePicker, JollyDateRangePicker };
export type { JollyDatePickerProps, JollyDateRangePickerProps };